import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IEmploymentState } from "./types";
import { IIDAStore } from "../types";
import { endOfDay, startOfDay } from "date-fns";
import { Employment, CreateEmploymentWithWorkHourPoolsPostback } from "../../models/employments";
import { employmentsService } from "../../services/Employments.service";
import type { BulkUpdateLeaveDaysRequest } from "@/api/models/BulkUpdateLeaveDaysRequest";
import type { BulkUpdateLeaveDaysResponse } from "@/api/models/BulkUpdateLeaveDaysResponse";
import { StoreCache, storeCachePeriode } from "../storeCache";
import { areTimeperiodsOverlapping, IPeriode } from "../../utils/dateUtils";
import store from "@/store";
import { UserApiData } from "@/modules/security/models/user-model";
import { useUser } from "@/composables/apiModels/useUser";
import { reactive } from "vue";

export const getInitialState = (): IEmploymentState => {
  return {
    searching: false,
    employments: new Map(),
    employmentsKeys: new Map(),
    storeCache: new StoreCache()
  };
};

const namespaced = true;

export const getters: GetterTree<IEmploymentState, IIDAStore> = {
  GetEmployments: (state) => (): Employment[] => {
    return Object.values(state.employments);
  },
  GetEmploymentsByPeriod: (state) => (from: Date, to: Date) => {
    return Object.values(state.employments).filter(
      (employment: Employment) =>
        employment.start_date <= to && endOfDay(employment.end_date) >= from
    );
  },
  GetEmploymentById: (state) => (employmentId: number) => {
    return state.employments[employmentId];
  },
  GetEmploymentByEmployeeIdAndDate: (state) => (employeeId: number, date: Date) => {
    return Object.values(state.employments).find(
      (employment: Employment) =>
        employment.employee_id === employeeId &&
        startOfDay(employment.start_date) <= startOfDay(date) &&
        date <= endOfDay(employment.end_date)
    );
  },
  GetEmploymentsByEmployeeId: (state) => (employeeId: number) => {
    const key = employeeId + "/";
    let employments = state.employmentsKeys[key];
    if (!employments) {
      employments = reactive([]);
    }
    return Object.values(employments);
  },
  GetEmploymentsByStartAndEndDate: (state) => (query: { startDate: Date; endDate: Date }) => {
    return Object.values(state.employments).filter((e) =>
      areTimeperiodsOverlapping(
        { fromDate: startOfDay(e.start_date), toDate: endOfDay(e.end_date) },
        {
          fromDate: startOfDay(query.startDate),
          toDate: endOfDay(query.endDate)
        }
      )
    );
  }
};

export const actions: ActionTree<IEmploymentState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchEmployments({ commit, state }, query: { from: Date; to: Date }): Promise<Employment[]> {
    return new Promise((resolve, reject) => {
      let from: Date = startOfDay(query.from);
      let to: Date = endOfDay(query.to);
      const user: UserApiData = store.getters["security/getUser"]();
      const { isEmployee, employmentsFrom, employmentsTo } = useUser();
      if (isEmployee(user)) {
        from = employmentsFrom();
        to = employmentsTo(user, to);
      }
      const periode = { fromDate: from, toDate: to } as IPeriode;
      if (!state.storeCache.isCached(periode)) {
        try {
          employmentsService.fetchEmployments(from, to).then((employments) => {
            commit("updateEmployments", { employments, from, to });
            return resolve(employments);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const employments = Object.values(state.employments).filter(
          (ab) => endOfDay(ab.end_date) >= from && ab.start_date < to
        );
        return resolve(employments);
      }
    });
  },
  createEmployment({ commit }, employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      employmentsService
        .createEmployment(employment)
        .then((newEmployment) => {
          commit("insertEmployment", newEmployment);
          resolve(newEmployment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createEmploymentWithWorkHourPools(
    { commit },
    query: { employment: Employment; copyWorkHourPoolsFrom: number }
  ): Promise<CreateEmploymentWithWorkHourPoolsPostback> {
    return new Promise((resolve, reject) => {
      employmentsService
        .createEmploymentWithWorkHourPools(query.employment, query.copyWorkHourPoolsFrom)
        .then((employmentPostback) => {
          commit("insertEmployment", employmentPostback.employment);

          resolve(employmentPostback);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateEmployment({ commit }, employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      employmentsService
        .updateEmployment(employment)
        .then((updatedEmployment) => {
          commit("updateEmployment", updatedEmployment);
          resolve(updatedEmployment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateEmploymentSoCalculatesAs({ commit }, employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      employmentsService
        .updateEmploymentSoCalculatesAs(employment)
        .then((updatedEmployment) => {
          commit("updateEmployment", updatedEmployment);
          resolve(updatedEmployment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async bulkUpdateLeaveDays(
    { commit, state },
    request: BulkUpdateLeaveDaysRequest
  ): Promise<BulkUpdateLeaveDaysResponse> {
    const response = await employmentsService.bulkUpdateLeaveDays(request);
    const updatedIds = new Set(response.updated ?? []);
    for (const update of request.updates ?? []) {
      if (update.employment_id !== undefined && updatedIds.has(update.employment_id)) {
        const cached = state.employments[update.employment_id];
        if (cached) {
          const next = Object.assign(new Employment(), cached);
          if (update.holidays_to_hold !== undefined) {
            next.holidays_to_hold = update.holidays_to_hold;
          }
          if (update.sixth_week_days_to_hold !== undefined) {
            next.sixth_week_days_to_hold = update.sixth_week_days_to_hold;
          }
          if (update.seniority_days_to_hold !== undefined) {
            next.seniority_days_to_hold = update.seniority_days_to_hold;
          }
          commit("updateEmployment", next);
        }
      }
    }
    return response;
  },
  deleteEmployment({ commit }, employmentId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      employmentsService
        .deleteEmployment(employmentId)
        .then((response) => {
          commit("deleteEmployment", employmentId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IEmploymentState> = {
  reset(state: IEmploymentState) {
    Object.assign(state, getInitialState());
  },
  insertEmployment(state: IEmploymentState, employment: Employment) {
    state.employments[employment.id] = employment;
    setEmploymentKey(state.employmentsKeys, employment.employee_id + "/", employment);
    // Add date-based keys for the employment period
    const startDay = startOfDay(employment.start_date);
    const endDay = endOfDay(employment.end_date);
    let currentDay = startDay;
    while (currentDay <= endDay) {
      setEmploymentKey(
        state.employmentsKeys,
        employment.employee_id + "/" + currentDay.getTime(),
        employment
      );
      currentDay = new Date(currentDay.getTime() + 24 * 60 * 60 * 1000); // Add one day
    }
  },
  updateEmployment(state: IEmploymentState, employment: Employment) {
    const oldEmployment: Employment = state.employments[employment.id];
    if (oldEmployment) {
      // Remove old keys
      delete state.employmentsKeys[oldEmployment.employee_id + "/"][employment.id];
      const oldStartDay = startOfDay(oldEmployment.start_date);
      const oldEndDay = endOfDay(oldEmployment.end_date);
      let currentDay = oldStartDay;
      while (currentDay <= oldEndDay) {
        if (state.employmentsKeys[oldEmployment.employee_id + "/" + currentDay.getTime()]) {
          delete state.employmentsKeys[oldEmployment.employee_id + "/" + currentDay.getTime()][
            employment.id
          ];
        }
        currentDay = new Date(currentDay.getTime() + 24 * 60 * 60 * 1000);
      }
    }
    // Add updated employment
    state.employments[employment.id] = employment;
    setEmploymentKey(state.employmentsKeys, employment.employee_id + "/", employment);
    // Add new date-based keys for the employment period
    const startDay = startOfDay(employment.start_date);
    const endDay = endOfDay(employment.end_date);
    let currentDay = startDay;
    while (currentDay <= endDay) {
      setEmploymentKey(
        state.employmentsKeys,
        employment.employee_id + "/" + currentDay.getTime(),
        employment
      );
      currentDay = new Date(currentDay.getTime() + 24 * 60 * 60 * 1000);
    }
  },
  deleteEmployment(state: IEmploymentState, employmentId: number) {
    const oldEmployment = state.employments[employmentId];
    if (oldEmployment) {
      delete state.employmentsKeys[oldEmployment.employee_id + "/"][employmentId];
      const startDay = startOfDay(oldEmployment.start_date);
      const endDay = endOfDay(oldEmployment.end_date);
      let currentDay = startDay;
      while (currentDay <= endDay) {
        if (state.employmentsKeys[oldEmployment.employee_id + "/" + currentDay.getTime()]) {
          delete state.employmentsKeys[oldEmployment.employee_id + "/" + currentDay.getTime()][
            employmentId
          ];
        }
        currentDay = new Date(currentDay.getTime() + 24 * 60 * 60 * 1000);
      }
    }
    delete state.employments[employmentId];
  },
  updateEmployments(
    state: IEmploymentState,
    payload: { employments: Employment[]; from: Date; to: Date }
  ) {
    for (const employment of payload.employments) {
      const frozenEmployment = Object.freeze(employment);
      if (!state.employments[frozenEmployment.id]) {
        store.commit("employments/insertEmployment", frozenEmployment);
      }
    }
    if (payload.from && payload.to) {
      state.storeCache.addCache(new storeCachePeriode({ fromDate: payload.from, toDate: payload.to }));
    }
  }
};

export const employmentsStoreModule: Module<IEmploymentState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};

function setEmploymentKey(statevalue, key, value) {
  const valueFromKey = statevalue[key];
  if (!valueFromKey) {
    statevalue[key] = { [value.id]: value };
  } else {
    valueFromKey[value.id] = value;
  }
}
